/**
 * Custom Authentication Service
 * Handles authentication flows using our custom email API with Resend
 */

// In Cloudflare Pages, API routes are served from the same domain
// Use relative URLs in production, absolute URLs in development
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '' // Empty string means same origin (Cloudflare Pages Functions)
  : 'http://localhost:3001';

class AuthService {
  /**
   * Send authentication email (signup, recovery, magiclink)
   * @param {string} email - User's email address
   * @param {string} type - Type of auth email ('signup', 'recovery', 'magiclink')
   * @param {string} password - User's password (for signup only)
   * @returns {Promise<Object>} - API response
   */
  async sendAuthEmail(email, type, password = null) {
    try {
      const requestBody = {
        email: email.trim().toLowerCase(),
        type: type
      };
      
      // Add password for signup requests
      if (type === 'signup' && password) {
        requestBody.password = password;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/auth/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `Failed to send ${type} email`);
      }

      return {
        success: true,
        data: data,
        message: data.message || `${type} email sent successfully`
      };
    } catch (error) {
      console.error(`Error sending ${type} email:`, error);
      return {
        success: false,
        error: error.message || `Failed to send ${type} email`,
        message: error.message || `Unable to send ${type} email. Please try again.`
      };
    }
  }

  /**
   * Send signup email
   * @param {string} email - User's email address
   * @param {string} password - User's password
   * @param {string} username - User's username
   * @param {string} gender - User's gender
   * @param {string} country - User's country
   * @param {string} dob - User's date of birth (ISO string)
   * @param {Object} consentInfo - Consent information (consent_type, consent_text_version, consent_text_snapshot)
   * @returns {Promise<Object>} - API response
   */
  async sendSignupEmail(email, password, username, gender = null, country = null, dob = null, consentInfo = null) {
    try {
      const requestBody = {
        email: email.trim().toLowerCase(),
        type: 'signup',
        password: password,
        username: username.trim()
      };
      
      // Add optional fields if provided
      if (gender) requestBody.gender = gender;
      if (country) requestBody.country = country;
      if (dob) requestBody.dob = dob;
      if (consentInfo) {
        requestBody.consent_type = consentInfo.consent_type;
        requestBody.consent_text_version = consentInfo.consent_text_version;
        requestBody.consent_text_snapshot = consentInfo.consent_text_snapshot;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/auth/send-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();

      if (!response.ok) {
        // Extract the error message from the response
        const errorMessage = data.message || data.error || 'Failed to send signup email';
        return {
          success: false,
          error: errorMessage,
          message: errorMessage
        };
      }

      return {
        success: true,
        data: data,
        message: data.message || 'Signup email sent successfully'
      };
    } catch (error) {
      console.error('Error sending signup email:', error);
      // If it's a network error or JSON parse error, use a generic message
      // Otherwise, use the error message
      const errorMessage = error.message && !error.message.includes('JSON') 
        ? error.message 
        : 'Unable to send signup email. Please try again.';
      return {
        success: false,
        error: errorMessage,
        message: errorMessage
      };
    }
  }

  /**
   * Send password recovery email
   * @param {string} email - User's email address
   * @returns {Promise<Object>} - API response
   */
  async sendRecoveryEmail(email) {
    return this.sendAuthEmail(email, 'recovery');
  }

  /**
   * Send magic link email
   * @param {string} email - User's email address
   * @returns {Promise<Object>} - API response
   */
  async sendMagicLinkEmail(email) {
    return this.sendAuthEmail(email, 'magiclink');
  }

  /**
   * Validate email format
   * @param {string} email - Email to validate
   * @returns {boolean} - Whether email is valid
   */
  isValidEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Get user-friendly error messages for different auth types
   * @param {string} type - Auth type
   * @param {string} error - Error message
   * @returns {string} - User-friendly error message
   */
  getErrorMessage(type, error) {
    if (!error) {
      return 'An error occurred. Please try again.';
    }

    // If the error message is already user-friendly (from backend), use it directly
    // Backend returns specific messages like "Username cannot contain spaces"
    const userFriendlyPatterns = [
      'cannot contain',
      'can only contain',
      'already taken',
      'already exists',
      'is required',
      'must be',
      'Please'
    ];
    
    const errorLower = error.toLowerCase();
    const isUserFriendly = userFriendlyPatterns.some(pattern => errorLower.includes(pattern));
    
    if (isUserFriendly) {
      return error; // Use the backend message directly
    }

    const errorMessages = {
      signup: {
        'User already registered': 'An account with this email already exists. Try signing in instead.',
        'Invalid email': 'Please enter a valid email address.',
        'Email sending failed': 'Unable to send verification email. Please try again.',
        'username cannot contain spaces': 'Username cannot contain spaces.',
        'username can only contain': 'Username can only contain letters, numbers, and underscores.',
        'username already taken': 'This username is already taken. Please choose another.',
        'invalid username': 'Username is invalid. Please check the requirements.',
        default: 'Unable to create your account. Please try again.'
      },
      recovery: {
        'User not found': 'No account found with this email address.',
        'Invalid email': 'Please enter a valid email address.',
        'Email sending failed': 'Unable to send password reset email. Please try again.',
        default: 'Unable to send password reset email. Please try again.'
      },
      magiclink: {
        'User not found': 'No account found with this email address.',
        'Invalid email': 'Please enter a valid email address.',
        'Email sending failed': 'Unable to send magic link. Please try again.',
        default: 'Unable to send magic link. Please try again.'
      }
    };

    const typeMessages = errorMessages[type] || errorMessages.signup;
    
    // Check for specific error messages
    for (const [key, message] of Object.entries(typeMessages)) {
      if (error.toLowerCase().includes(key.toLowerCase())) {
        return message;
      }
    }

    return typeMessages.default;
  }
}

// Create and export a singleton instance
const authService = new AuthService();
export default authService;